import {
  CreateStartUpPageContainer,
  OsEventTypeList,
  TextContainerProperty,
  TextContainerUpgrade,
  waitForEvenAppBridge,
} from "@evenrealities/even_hub_sdk";
import "./styles.css";

type NewsItem = {
  id: string;
  title: string;
  url: string;
  source: string;
  publishedAt: string;
  summary: string;
  tags: string[];
};

type NewsCategory = {
  slug: string;
  label: string;
  color: string;
  summary: string;
  keyPoints: string[];
  items: NewsItem[];
};

type NewsPayload = {
  generatedAt: string;
  since: string;
  categories: NewsCategory[];
};

type EvenBridge = Awaited<ReturnType<typeof waitForEvenAppBridge>>;

const STORAGE_API_URL = "evenAiNews.apiUrl";
const STORAGE_TOKEN = "evenAiNews.token";
const GLASS_CONTAINER_ID = 1;
const GLASS_CONTAINER_NAME = "main";

const elements = {
  refreshButton: document.querySelector<HTMLButtonElement>("#refreshButton")!,
  saveSettingsButton: document.querySelector<HTMLButtonElement>("#saveSettingsButton")!,
  apiUrlInput: document.querySelector<HTMLInputElement>("#apiUrlInput")!,
  tokenInput: document.querySelector<HTMLInputElement>("#tokenInput")!,
  statusText: document.querySelector<HTMLElement>("#statusText")!,
  statusDot: document.querySelector<HTMLElement>("#statusDot")!,
  generatedAt: document.querySelector<HTMLElement>("#generatedAt")!,
  themeTabs: document.querySelector<HTMLElement>("#themeTabs")!,
  newsList: document.querySelector<HTMLElement>("#newsList")!,
};

const state = {
  apiUrl: localStorage.getItem(STORAGE_API_URL) || import.meta.env.VITE_NEWS_API_URL || "",
  token: localStorage.getItem(STORAGE_TOKEN) || import.meta.env.VITE_NEWS_API_TOKEN || "",
  payload: null as NewsPayload | null,
  categoryIndex: 0,
  itemIndex: 0,
  bridge: null as EvenBridge | null,
  g2Ready: false,
};

elements.apiUrlInput.value = state.apiUrl;
elements.tokenInput.value = state.token;

function setStatus(message: string, variant: "idle" | "loading" | "ready" | "error" = "idle") {
  elements.statusText.textContent = message;
  elements.statusDot.dataset.variant = variant;
}

function apiBase() {
  return state.apiUrl.replace(/\/$/, "");
}

function requestHeaders() {
  const headers: Record<string, string> = {};
  if (state.token) headers.Authorization = `Bearer ${state.token}`;
  return headers;
}

function activeCategory() {
  return state.payload?.categories[state.categoryIndex] || null;
}

function activeItem() {
  const category = activeCategory();
  return category?.items[state.itemIndex] || null;
}

function clampIndexes() {
  const categories = state.payload?.categories || [];
  state.categoryIndex = Math.min(Math.max(state.categoryIndex, 0), Math.max(categories.length - 1, 0));
  const items = categories[state.categoryIndex]?.items || [];
  state.itemIndex = Math.min(Math.max(state.itemIndex, 0), Math.max(items.length - 1, 0));
}

function cycleCategory(direction = 1) {
  const categories = state.payload?.categories || [];
  if (!categories.length) return;
  state.categoryIndex = (state.categoryIndex + direction + categories.length) % categories.length;
  state.itemIndex = 0;
  render();
  updateGlasses();
}

function cycleItem(direction = 1) {
  const category = activeCategory();
  if (!category?.items.length) return;
  state.itemIndex = (state.itemIndex + direction + category.items.length) % category.items.length;
  render();
  updateGlasses();
}

function truncate(value: string, maxLength: number) {
  const clean = value.replace(/\s+/g, " ").trim();
  return clean.length > maxLength ? `${clean.slice(0, maxLength - 1)}…` : clean;
}

function relativeDate(iso: string) {
  const published = new Date(iso).getTime();
  if (Number.isNaN(published)) return "";
  const diffHours = Math.max(0, Math.floor((Date.now() - published) / 36e5));
  if (diffHours < 1) return "1時間以内";
  if (diffHours < 24) return `${diffHours}時間前`;
  return `${Math.floor(diffHours / 24)}日前`;
}

async function loadNews(forceRefresh = false) {
  if (!state.apiUrl) {
    setStatus("Cloudflare Worker APIを設定してください", "error");
    updateGlasses("API URLを設定してください。\nスマホ画面で Cloudflare Worker URL を保存します。");
    return;
  }

  setStatus(forceRefresh ? "Workerで最新情報を再収集中" : "最新情報を取得中", "loading");
  elements.refreshButton.disabled = true;
  try {
    if (forceRefresh) {
      const refresh = await fetch(`${apiBase()}/api/refresh`, {
        method: "POST",
        headers: requestHeaders(),
      });
      if (!refresh.ok) throw new Error(`refresh ${refresh.status}`);
    }

    const response = await fetch(`${apiBase()}/api/news`, {
      cache: "no-store",
      headers: requestHeaders(),
    });
    if (!response.ok) throw new Error(`news ${response.status}`);

    state.payload = (await response.json()) as NewsPayload;
    clampIndexes();
    setStatus("G2に表示できます", state.g2Ready ? "ready" : "idle");
    render();
    updateGlasses();
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    setStatus(`取得失敗: ${message}`, "error");
    updateGlasses(`G2 AI News\n\n取得失敗:\n${message}\n\nスマホ側のAPI URL、token、Worker CORSを確認してください。`);
  } finally {
    elements.refreshButton.disabled = false;
  }
}

function render() {
  const payload = state.payload;
  if (!payload) {
    elements.themeTabs.innerHTML = "";
    elements.newsList.innerHTML = `<div class="empty">Cloudflare Worker APIを設定して最新情報を読み込みます。</div>`;
    elements.generatedAt.textContent = "";
    return;
  }

  elements.generatedAt.textContent = `最終更新 ${new Date(payload.generatedAt).toLocaleString("ja-JP")}`;
  elements.themeTabs.innerHTML = payload.categories
    .map(
      (category, index) => `
        <button
          class="theme-tab ${index === state.categoryIndex ? "is-active" : ""}"
          type="button"
          data-index="${index}"
          style="--theme:${category.color}"
        >
          ${escapeHtml(category.label)}
        </button>
      `,
    )
    .join("");

  const category = activeCategory();
  if (!category) {
    elements.newsList.innerHTML = `<div class="empty">表示できるテーマがありません。</div>`;
    return;
  }

  const keyPoints = category.keyPoints
    .slice(0, 3)
    .map((point) => `<li>${escapeHtml(point)}</li>`)
    .join("");
  const cards = category.items
    .map(
      (item, index) => `
        <article class="news-card ${index === state.itemIndex ? "is-current" : ""}">
          <div class="news-card__meta">
            <span>${escapeHtml(item.source)}</span>
            <span>${relativeDate(item.publishedAt)}</span>
          </div>
          <h2>${escapeHtml(item.title)}</h2>
          <p>${escapeHtml(item.summary)}</p>
          <div class="news-card__tags">${item.tags
            .slice(0, 4)
            .map((tag) => `<span>${escapeHtml(tag)}</span>`)
            .join("")}</div>
          <a href="${escapeHtml(item.url)}" target="_blank" rel="noreferrer">元記事を開く</a>
        </article>
      `,
    )
    .join("");

  elements.newsList.innerHTML = `
    <section class="category-summary">
      <div class="category-summary__head">
        <span style="background:${category.color}"></span>
        <strong>${escapeHtml(category.label)}</strong>
        <em>${category.items.length}件</em>
      </div>
      <p>${escapeHtml(category.summary)}</p>
      ${keyPoints ? `<ul>${keyPoints}</ul>` : ""}
    </section>
    ${cards || `<div class="empty">このテーマの新着はまだありません。</div>`}
  `;
}

function escapeHtml(value: string) {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function buildGlassText(override?: string) {
  if (override) return truncate(override, 950);
  const payload = state.payload;
  const category = activeCategory();
  const item = activeItem();
  if (!payload || !category) {
    return "G2 AI News\n\nスマホ側でCloudflare Worker APIを設定してください。";
  }
  if (!item) {
    return `G2 AI News\n\n${category.label}\n\n新着はまだありません。\n\n2回タップ: テーマ切替`;
  }

  const position = `${state.itemIndex + 1}/${category.items.length}`;
  const categoryPosition = `${state.categoryIndex + 1}/${payload.categories.length}`;
  const points = category.keyPoints.slice(0, 2).map((point) => `・${point}`).join("\n");
  return truncate(
    [
      `G2 AI News  ${categoryPosition}`,
      `${category.label}  ${position}`,
      "",
      item.title,
      `${item.source} / ${relativeDate(item.publishedAt)}`,
      "",
      item.summary,
      points ? `\n要点\n${points}` : "",
      "",
      "Tap: 次の記事  Double: 次テーマ",
    ]
      .filter(Boolean)
      .join("\n"),
    950,
  );
}

async function initGlasses() {
  try {
    const bridge = await Promise.race([
      waitForEvenAppBridge(),
      new Promise<never>((_, reject) => setTimeout(() => reject(new Error("bridge timeout")), 1800)),
    ]);
    state.bridge = bridge;
    state.g2Ready = true;

    const mainText = new TextContainerProperty({
      xPosition: 0,
      yPosition: 0,
      width: 576,
      height: 288,
      borderWidth: 0,
      borderColor: 5,
      paddingLength: 8,
      containerID: GLASS_CONTAINER_ID,
      containerName: GLASS_CONTAINER_NAME,
      content: buildGlassText(),
      isEventCapture: 1,
    });

    await bridge.createStartUpPageContainer(
      new CreateStartUpPageContainer({
        containerTotalNum: 1,
        textObject: [mainText],
      }),
    );

    bridge.onEvenHubEvent((event) => {
      const eventType = event.textEvent?.eventType;
      switch (eventType) {
        case OsEventTypeList.DOUBLE_CLICK_EVENT:
          cycleCategory(1);
          break;
        case OsEventTypeList.SCROLL_TOP_EVENT:
          cycleItem(-1);
          break;
        case OsEventTypeList.SCROLL_BOTTOM_EVENT:
        case OsEventTypeList.CLICK_EVENT:
        case undefined:
          cycleItem(1);
          break;
      }
    });

    setStatus("G2ブリッジ接続済み", "ready");
    updateGlasses();
  } catch {
    state.g2Ready = false;
    setStatus("ブラウザプレビュー中", "idle");
  }
}

async function updateGlasses(override?: string) {
  if (!state.bridge || !state.g2Ready) return;
  const content = buildGlassText(override);
  await state.bridge.textContainerUpgrade(
    new TextContainerUpgrade({
      containerID: GLASS_CONTAINER_ID,
      containerName: GLASS_CONTAINER_NAME,
      contentOffset: 0,
      contentLength: content.length,
      content,
    }),
  );
}

elements.refreshButton.addEventListener("click", () => loadNews(true));
elements.saveSettingsButton.addEventListener("click", () => {
  state.apiUrl = elements.apiUrlInput.value.trim();
  state.token = elements.tokenInput.value.trim();
  localStorage.setItem(STORAGE_API_URL, state.apiUrl);
  localStorage.setItem(STORAGE_TOKEN, state.token);
  loadNews();
});
elements.themeTabs.addEventListener("click", (event) => {
  const button = (event.target as HTMLElement).closest<HTMLButtonElement>(".theme-tab");
  if (!button) return;
  state.categoryIndex = Number(button.dataset.index || 0);
  state.itemIndex = 0;
  render();
  updateGlasses();
});

render();
initGlasses();
loadNews();
